import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2 } from '@mui/material';
import ButtonComponent from 'components/ButtonComponent';
import DropdownComponent from 'components/DropdownComponent';
import ModalComponent from 'components/ModalComponent';
import TextFieldComponent from 'components/TextFieldComponent';
import { AppContext } from 'context/AppContextProvider';
import { AlarmCentralVirtualContext } from 'pages/AlarmCentralPages/context/AlarmCentralVirtualContext';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useCreateDeviceToVirtualAlarmCentral,
  useUpdateDeviceToVirtualAlarmCentral,
} from 'services/AlarmCentralService/Virtual/hooks/useMutation';
import { VirtualAlarmCentralDeviceFormSchema } from 'services/AlarmCentralService/Virtual/schemas';
import { useGetAllContactIdPossibleEventByLicense } from 'services/ContactIdPossibleEventService/hooks/useQuery';
import {
  useGetAllDevicesByLicenseAsync,
  useGetAllByLicenseInfoAsync,
} from 'services/Devices/hooks/useMutation';
import { IDeviceProps } from 'services/Devices/types';
import zod from 'zod';

interface VirtualAlarmCentralDeviceEditionModalProps {
  isOpen: boolean;
  onClose: () => void;
  partitionId: number;
  zone: number;
}

type VirtualAlarmCentralDeviceEditionModalFormInput = zod.infer<
  typeof VirtualAlarmCentralDeviceFormSchema
>;

const VirtualAlarmCentralDeviceEditionModal = ({
  isOpen,
  onClose,
  partitionId,
  zone,
}: VirtualAlarmCentralDeviceEditionModalProps) => {
  const {
    control,
    watch,
    setValue,
    getValues,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<VirtualAlarmCentralDeviceEditionModalFormInput>({
    resolver: zodResolver(VirtualAlarmCentralDeviceFormSchema),
    defaultValues: {
      name: '',
    },
  });
  const { alarmCentral } = useContext(AlarmCentralVirtualContext);
  const { showSnackbar } = useContext(AppContext);
  const { data: contactIdEvents } = useGetAllContactIdPossibleEventByLicense();
  const [devices, setDevices] = useState<IDeviceProps[]>([]);
  const [contactIdPossibleEvents, setContactIdPossibleEvents] = useState<
    {
      description: string;
      value: string;
    }[]
  >();

  
  const { mutateAsync: mutateGetAllDevices } = useGetAllByLicenseInfoAsync();
  const {
    mutateAsync: mutateCreateDeviceToVirtualAlarmCentral,
    isLoading: isLoadingCreateDeviceToVirtualAlarmCentral,
  } = useCreateDeviceToVirtualAlarmCentral();
  const {
    mutateAsync: mutateUpdateDeviceToVirtualAlarmCentral,
    isLoading: isLoadingUpdateDeviceToVirtualAlarmCentral,
  } = useUpdateDeviceToVirtualAlarmCentral();

  const onCloseComponent = () => {
    reset();
    onClose();
  };
  const handleSaveDevice = (
    device: VirtualAlarmCentralDeviceEditionModalFormInput,
  ) => {
    if (alarmCentral.id) {
      if (!device.id)
        mutateCreateDeviceToVirtualAlarmCentral(
          {
            alarmCentralId: alarmCentral.id,
            device,
          },
          {
            onSuccess: () => {
              showSnackbar('Dispositivo vinculado com sucesso.', 'success');
              onCloseComponent();
            },
          },
        );
      else
        mutateUpdateDeviceToVirtualAlarmCentral(
          { device, id: device.id },
          {
            onSuccess: () => {
              showSnackbar('Dispositivo atualizado com sucesso.', 'success');
              onCloseComponent();
            },
          },
        );
    }
  };

  const onChangeSelectedDevice = (
    onChange: (...event: any[]) => void,
    deviceId: any,
  ) => {
    onChange(deviceId);
    // if()
  };

  useEffect(() => {
    if (contactIdEvents) {
      const evt: { description: string; value: string }[] | undefined =
        contactIdEvents.map((v) => {
          return {
            description: v.code + ' - ' + v.description,
            value: v.id.toString(),
          };
        });
      setContactIdPossibleEvents(evt);
    }
  }, [contactIdEvents]);

  useEffect(() => {
    mutateGetAllDevices({}).then(setDevices);
  }, []);

  useEffect(() => {
    setValue('partitionId', partitionId);
    if (!getValues('zone')) setValue('zone', zone);
  }, [partitionId, zone]);

  const deviceIdWatch = watch('deviceId');

  useEffect(() => {
    if (deviceIdWatch) {
      const selectedDevice = devices.find((d) => d.id === deviceIdWatch);
      if (selectedDevice) {
        setValue('name', selectedDevice.name);
        if (selectedDevice.type) setValue('deviceType', selectedDevice.type);
      }
    }
  }, [deviceIdWatch]);

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onCloseComponent}
      title="Edição de Dispositivo"
    >
      <form onSubmit={handleSubmit(handleSaveDevice)}>
        <Controller
          render={({ field: { onChange, value } }) => (
            <DropdownComponent
              options={devices ?? []}
              optionValue="id"
              label="Dispositivo"
              value={value}
              optionLabel="name"
              onChange={(val) => onChangeSelectedDevice(onChange, val)}
            />
          )}
          control={control}
          name="deviceId"
        />
        <Controller
          render={({ field: { onChange, value } }) => (
            <TextFieldComponent
              label="Nome identificador"
              value={value}
              onChangeText={onChange}
            />
          )}
          control={control}
          name="name"
        />
        <Controller
          render={({ field: { onChange, value } }) => (
            <DropdownComponent
              options={contactIdPossibleEvents ?? []}
              optionValue="value"
              label="Evento de disparo (CONTACT ID)"
              value={value}
              optionLabel="description"
              onChange={(val) => onChange(Number.parseInt(val))}
            />
          )}
          control={control}
          name="contactIdEventId"
        />
        <Grid2
          container
          sx={{ display: 'flex', justifyContent: 'end' }}
          columnSpacing={1}
          rowSpacing={1}
        >
          <Grid2 size={{ xs: 12, md: 3 }}>
            <ButtonComponent
              fullWidth
              type="submit"
              loading={
                isLoadingCreateDeviceToVirtualAlarmCentral ||
                isLoadingUpdateDeviceToVirtualAlarmCentral
              }
            >
              Salvar
            </ButtonComponent>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 3 }}>
            <ButtonComponent
              color="error"
              fullWidth
              type="button"
              onClick={() => onCloseComponent()}
              loading={
                isLoadingCreateDeviceToVirtualAlarmCentral ||
                isLoadingUpdateDeviceToVirtualAlarmCentral
              }
            >
              Cancelar
            </ButtonComponent>
          </Grid2>
        </Grid2>
      </form>
    </ModalComponent>
  );
};

export default VirtualAlarmCentralDeviceEditionModal;
